import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Database } from 'lucide-react';

export function LoginForm() {
  const { login, isLoggingIn, loginError, demoLogin, isDemoLoggingIn } = useAuth();
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(credentials);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleInputChange = (field: keyof typeof credentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleDevLogin = async () => {
    try {
      await demoLogin();
      // Redirect happens automatically if AuthProvider/AppRoutes reacts to state change,
      // but in some custom router setups it might need manual trigger.
      // Based on AppRoutes, being authenticated should enable the dashboard.
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Dev login failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Database className="w-8 h-8 text-shiftleft-600" />
            <span className="text-2xl font-bold">ShiftLeft Digital</span>
          </div>
          <CardTitle>Sign In</CardTitle>
          <CardDescription>
            Enter your credentials to access the SQL Health monitoring platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                value={credentials.email}
                onChange={handleInputChange('email')}
                placeholder="Enter your email"
                required
                disabled={isLoggingIn || isDemoLoggingIn}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={handleInputChange('password')}
                placeholder="Enter your password"
                required
                disabled={isLoggingIn || isDemoLoggingIn}
              />
            </div>

            {loginError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {loginError instanceof Error ? loginError.message : 'Login failed. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoggingIn || isDemoLoggingIn || !credentials.email || !credentials.password}
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

            {/* Development bypass */}
            {import.meta.env.DEV && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">Development Mode:</p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full transition-all hover:bg-shiftleft-50 hover:text-shiftleft-600 border-shiftleft-200"
                  onClick={handleDevLogin}
                  disabled={isLoggingIn || isDemoLoggingIn}
                >
                  {isDemoLoggingIn ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Initializing...
                    </>
                  ) : (
                    'Login as Admin (Dev)'
                  )}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}